import type { RefObject } from 'preact';
import { useEffect, useRef, useContext, useCallback } from 'preact/hooks';
import { ConveyorBeltContext } from './ConveyorBeltContext';
import { useId } from '../hooks/UNSAFE_useId';

type ConveyorBeltItemOptions<T extends HTMLElement> = {
  /**
   * Specifies the current item which should be scrolled into view
   */
  isCurrent?: boolean;
  /**
   * Conevyor belt item reference, if not provided is created in the hook
   */
  itemRef?: RefObject<T>;
};

/**
 * It is a convenience hook for interacting with ConveyorBeltContext.
 * Marks ConveyorBelt item and specifies current item to be scrolled into view.
 * The hook returns the necessary props: ref for ConveyorBelt child component
 * and 'data-oj-conveyorbelt-item' data attribute to mark conveyorbelt item.
 * If the item is set as current, then it is scrolled into the view.
 * If the hook detects ConveyorBeltContext then it should apply the methods of the ConveyorBeltContext.
 * @param options ConveyorBeltItemOptions: isCurrent boolean,
 * specifies whether the item is current and should be scrolled into the view and
 * itemRef Conevyor belt item reference which if not provided is created in the hook
 * @returns ref for ConveyorBelt child component
 * and 'data-oj-conveyorbelt-item' data attribute to mark conveyorbelt item.
 */
export function useConveyorBeltItem<T extends HTMLElement = HTMLDivElement>(
  options: ConveyorBeltItemOptions<T>
) {
  const { isCurrent, itemRef } = options;
  const context = useContext(ConveyorBeltContext);
  const ref = useRef<T>(null);
  const prevIsCurrent = useRef<boolean | undefined>(undefined);

  const getResolvedRef = useCallback(() => {
    return itemRef ?? ref;
  }, [itemRef]);

  // Generate a unique id for this item
  const id = useId();

  // When isCurrent, set this as the active item in context
  useEffect(() => {
    if (
      context &&
      context.setActiveItemId &&
      isCurrent &&
      !prevIsCurrent.current // Only when isCurrent changes from false to true
    ) {
      context.setActiveItemId(id);
    }
    prevIsCurrent.current = isCurrent;
  }, [context, isCurrent, id]);

  // Only call setCurrentItem if this item is the active one
  useEffect(() => {
    if (
      getResolvedRef().current &&
      context &&
      context.setCurrentItem &&
      context.activeItemId === id
    ) {
      context.setCurrentItem(getResolvedRef().current);
    }
  }, [context, id, context?.activeItemId, getResolvedRef]);

  const conveyorBeltItemProps = {
    ref: ref,
    ...(context && { 'data-oj-conveyorbelt-item': id })
  };

  return conveyorBeltItemProps;
}
